import { Link } from "react-router-dom";
import { Stethoscope, LogOut, LayoutDashboard, Shield, KeyRound } from "lucide-react";
import { useState } from "react";
import { useAuth, isMember } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SetPasswordDialog } from "@/components/SetPasswordDialog";

export function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-home-link">
      <span className="h-9 w-9 rounded-xl bg-teal-600 text-white flex items-center justify-center shadow-sm group-hover:bg-teal-700 transition-colors">
        <Stethoscope className="h-5 w-5" aria-hidden />
      </span>
      <span className="font-display font-extrabold text-lg tracking-tight text-slate-900">Bedside to Remote</span>
    </Link>
  );
}

export function SiteHeader({ links = true }) {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Brand />
        {links && !isMember(user) && (
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600" aria-label="Main">
            <a href="#modules" className="hover:text-teal-700 transition-colors" data-testid="nav-modules">Modules</a>
            <a href="#who" className="hover:text-teal-700 transition-colors" data-testid="nav-who">Who it's for</a>
            <a href="#included" className="hover:text-teal-700 transition-colors" data-testid="nav-included">What's included</a>
            <a href="#faq" className="hover:text-teal-700 transition-colors" data-testid="nav-faq">FAQ</a>
          </nav>
        )}
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="user-menu-button">{user.email}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {isMember(user) && <DropdownMenuItem asChild><Link to="/dashboard" data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />My course</Link></DropdownMenuItem>}
              {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" />Admin</Link></DropdownMenuItem>}
              <DropdownMenuItem onSelect={() => setPwOpen(true)} data-testid="menu-set-password"><KeyRound className="h-4 w-4 mr-2" />{user.has_password ? "Change password" : "Set a password"}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={logout} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to="/login" className="text-sm font-medium text-teal-800 hover:text-teal-900 px-3 py-2 rounded-full hover:bg-teal-50 transition-colors" data-testid="header-signin-link">Sign in</Link>
        )}
      </div>
      <SetPasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </header>
  );
}

export function SiteFooter() {
  const links = [["terms", "Terms"], ["privacy", "Privacy"], ["refund", "Refund policy"], ["disclaimer", "Disclaimer"], ["ai", "AI disclosure"], ["cookies", "Cookies"]];
  return (
    <footer className="border-t border-slate-200 bg-white mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 grid gap-8 md:grid-cols-[1.2fr_1fr]">
        <div className="space-y-3">
          <Brand />
          <p className="text-sm text-slate-600 max-w-md" data-testid="footer-disclaimer">
            This course is educational career guidance. It does not guarantee employment and does not provide medical, legal or financial advice.
          </p>
          <p className="text-xs text-slate-500">AI is used in this course: the video presenter is AI-generated, and the Career Coach is an AI assistant.</p>
        </div>
        <nav className="flex flex-wrap gap-x-6 gap-y-3 text-sm md:justify-end content-start" aria-label="Legal">
          {links.map(([s, l]) => (
            <Link key={s} to={`/legal/${s}`} className="text-slate-600 hover:text-teal-700 underline-offset-4 hover:underline" data-testid={`footer-link-${s}`}>{l}</Link>
          ))}
        </nav>
      </div>
      <div className="text-center text-xs text-slate-500 pb-8">© {new Date().getFullYear()} Bedside to Remote</div>
    </footer>
  );
}
